import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plane, Ship, Truck, Warehouse, ShieldCheck, ShieldAlert, FileCheck, PackageOpen, Award, ArrowUpRight, Car, Cpu, HeartPulse, Leaf, Factory, ShoppingBag, CheckCircle2 } from 'lucide-react';
import { services as defServices, industries as defIndustries, stats as defStats, heroImages, experienceHighlights } from '../mock/data';
import { useSiteSettings } from '../context/SiteSettingsContext';

const iconMap = { Plane, Ship, Truck, Warehouse, ShieldCheck, ShieldAlert, TruckIcon: Truck, FileCheck, PackageOpen, Car, Cpu, HeartPulse, Leaf, Factory, ShoppingBag };

const Counter = ({ value, suffix, label }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const duration = 1600;
        const step = (t0) => {
          const t = performance.now() - t0;
          const p = Math.min(t / duration, 1);
          setN(Math.floor(value * (0.5 - Math.cos(Math.PI * p) / 2)));
          if (p < 1) requestAnimationFrame(() => step(t0));
        };
        step(performance.now());
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [value]);
  return (
    <div ref={ref} className="text-center border-l border-white/10 first:border-l-0 px-6">
      <div className="font-serif-display text-5xl md:text-6xl font-bold text-white mb-2">{n}<span className="text-[#c8102e]">{suffix}</span></div>
      <div className="text-white/60 text-xs uppercase tracking-[0.25em] font-medium">{label}</div>
    </div>
  );
};

export const AboutSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <div className="relative">
        <img src={heroImages.ship} alt="Container ship" className="w-full h-[520px] object-cover" />
        <img src={heroImages.warehouse} alt="Warehouse" className="hidden md:block absolute -bottom-12 -right-6 w-56 h-56 object-cover border-8 border-white shadow-2xl" />
        <div className="absolute -top-6 -left-6 bg-[#c8102e] text-white px-7 py-5">
          <div className="font-serif-display text-4xl font-bold">11<span className="text-white/80">+</span></div>
          <div className="text-[10px] uppercase tracking-[0.25em] mt-1">Years of Excellence</div>
        </div>
      </div>
      <div>
        <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">The Lanka Express Story</span></div>
        <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-black mb-6 leading-[1.05]">A legacy built on <em className="text-[#c8102e] not-italic">trust</em>.</h2>
        <p className="text-neutral-600 mb-5 leading-relaxed text-[15px]">
          Headquartered in Milan, Lanka Express stands at the heart of a global trade network built over more than a decade. Founded in 2014 to connect Sri Lankan families in Europe with home, we have grown into a premium logistics partner — trusted by tens of thousands of families and businesses across four continents.
        </p>
        <p className="text-neutral-600 mb-8 leading-relaxed text-[15px]">
          Every shipment we handle carries something more than cargo: it carries memory, care and love. That is why we treat each parcel with the precision of a master craftsman and the warmth of family.
        </p>
        <div className="grid grid-cols-2 gap-4 mb-8">
          {['Air Freight', 'Sea Freight', 'Road Transport', '3PL & Warehousing'].map((f) => (
            <div key={f} className="flex items-center gap-3">
              <CheckCircle2 className="w-4 h-4 text-[#c8102e] shrink-0" />
              <span className="font-medium text-black text-sm">{f}</span>
            </div>
          ))}
        </div>
        <Link to="/about" className="group inline-flex items-center gap-3 bg-black hover:bg-[#c8102e] text-white font-semibold h-12 px-8 text-[13px] tracking-[0.15em] uppercase transition-colors">
          Discover Our Journey <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </Link>
      </div>
    </div>
  </section>
);

export const StatsSection = () => {
  const { getContent } = useSiteSettings();
  const stats = getContent('stats', defStats);
  return (
  <section className="py-20 bg-black relative overflow-hidden">
    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url(${heroImages.bg})`, backgroundSize: 'cover' }} />
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-black/80" />
    <div className="relative max-w-7xl mx-auto px-6">
      <div className="text-center mb-16">
        <div className="flex items-center gap-3 justify-center mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">By The Numbers</span><span className="h-px w-10 bg-[#c8102e]" /></div>
        <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-white">A track record you can trust</h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
        {stats.map((s) => <Counter key={s.label} {...s} />)}
      </div>
    </div>
  </section>
  );
};

export const ExperienceTimeline = () => (
  <section className="py-24 bg-neutral-50">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-16 max-w-2xl mx-auto">
        <div className="flex items-center gap-3 justify-center mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Our Journey</span><span className="h-px w-10 bg-[#c8102e]" /></div>
        <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-black mb-4">Eleven years of moving what matters</h2>
        <p className="text-neutral-600 leading-relaxed">From a single office in Milan to a global network — our story is written in the trust of every family and business we serve.</p>
      </div>
      <div className="relative">
        <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-neutral-200" />
        <div className="space-y-10 md:space-y-16">
          {experienceHighlights.map((e, i) => (
            <div key={e.year} className={`md:grid md:grid-cols-2 md:gap-16 items-center ${i % 2 === 0 ? '' : 'md:[direction:rtl]'}`}>
              <div className={`md:[direction:ltr] ${i % 2 === 0 ? 'md:text-right' : ''}`}>
                <div className="font-serif-display text-6xl font-bold text-[#c8102e] mb-2">{e.year}</div>
                <h3 className="font-serif-display text-2xl font-bold text-black mb-3">{e.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{e.desc}</p>
              </div>
              <div className="hidden md:flex items-center justify-center">
                <div className="w-4 h-4 rounded-full bg-[#c8102e] ring-8 ring-white shadow-md" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const ServicesSection = ({ compact = false }) => {
  const { getContent } = useSiteSettings();
  const services = getContent('services', defServices);
  return (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-4">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">What We Offer</span></div>
          <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-black leading-[1.05]">Empower your business with premium logistics.</h2>
        </div>
        <p className="text-neutral-600 md:max-w-md text-[15px] leading-relaxed">From UPB clearances to bonded trucks, warehousing and cargo insurance — we optimise every step of your supply chain.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200">
        {(compact ? services.slice(0, 6) : services).map((s) => {
          const Icon = iconMap[s.icon] || Plane;
          return (
            <div key={s.title} className="group bg-white p-8 hover:bg-black hover:text-white transition-colors duration-300">
              <div className="w-12 h-12 border border-neutral-200 group-hover:border-[#c8102e] flex items-center justify-center mb-6 transition-colors">
                <Icon className="w-5 h-5 text-[#c8102e]" />
              </div>
              <h3 className="font-serif-display text-2xl font-bold mb-3">{s.title}</h3>
              <p className="text-sm text-neutral-600 group-hover:text-white/70 leading-relaxed">{s.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
  );
};

export const ISOSection = () => (
  <section className="py-24 bg-neutral-50">
    <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <div className="flex justify-center">
        <div className="relative">
          <div className="w-72 h-72 rounded-full border-8 border-[#c8102e]/10 flex items-center justify-center">
            <div className="w-56 h-56 rounded-full bg-white shadow-2xl flex items-center justify-center flex-col gap-2 border border-neutral-100">
              <Award className="w-20 h-20 text-[#c8102e]" />
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-semibold">Certified</div>
            </div>
          </div>
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-black text-white px-6 py-2 font-serif-display font-bold shadow-xl">ISO 9001:2015</div>
        </div>
      </div>
      <div>
        <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Quality Certified</span></div>
        <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-black mb-6 leading-[1.05]">Certified excellence you can measure.</h2>
        <p className="text-neutral-600 mb-4 leading-relaxed text-[15px]">
          Our ISO 9001:2015 certification is more than a badge — it is a promise. Every process, every parcel and every customer interaction is measured against the highest international standards.
        </p>
        <p className="text-neutral-600 leading-relaxed text-[15px]">
          From meticulous order handling to timely delivery, our relentless pursuit of quality permeates every facet of our operations.
        </p>
      </div>
    </div>
  </section>
);

export const IndustriesSection = () => {
  const { getContent } = useSiteSettings();
  const industries = getContent('industries', defIndustries);
  return (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-14 max-w-2xl mx-auto">
        <div className="flex items-center gap-3 justify-center mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Industries We Serve</span><span className="h-px w-10 bg-[#c8102e]" /></div>
        <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-black">Your global freight forwarder</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-neutral-200">
        {industries.map((i) => {
          const Icon = iconMap[i.icon] || Car;
          return (
            <div key={i.name} className="group bg-white p-8 text-center hover:bg-[#c8102e] transition-colors cursor-pointer">
              <Icon className="w-10 h-10 text-black group-hover:text-white mx-auto mb-4 transition-colors" />
              <h4 className="font-serif-display font-bold text-black group-hover:text-white text-lg transition-colors">{i.name}</h4>
            </div>
          );
        })}
      </div>
    </div>
  </section>
  );
};

export const TeamGallery = () => {
  const { settings } = useSiteSettings();
  const pic = (slot, fb) => settings?.[`image_${slot}`] || fb;
  return (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-14 max-w-2xl mx-auto">
        <div className="flex items-center gap-3 justify-center mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Meet Our Team</span><span className="h-px w-10 bg-[#c8102e]" /></div>
        <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-black mb-4 leading-[1.05]">The people behind every delivery</h2>
        <p className="text-neutral-600 leading-relaxed">From our container yards to our customer service counters — meet the professionals who make Lanka Express a name families trust.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { img: pic('staff_containers', '/images/staff_containers.jpg'), label: 'Port Operations', sub: 'Container yard team' },
          { img: pic('staff_warehouse', '/images/staff_warehouse.jpg'), label: 'Warehouse Team', sub: 'Sorting & inventory' },
          { img: pic('staff_truck', '/images/staff_truck.jpg'), label: 'Fleet & Delivery', sub: 'Branded fleet nationwide' },
          { img: pic('staff_counter', '/images/staff_counter.jpg'), label: 'Customer Service', sub: 'Always here to help' },
        ].map((t) => (
          <div key={t.label} className="group relative overflow-hidden aspect-[3/4]">
            <img src={t.img} alt={t.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-90" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#c8102e] font-semibold mb-1">{t.sub}</div>
              <h3 className="font-serif-display text-2xl font-bold">{t.label}</h3>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

const Sections = () => null;
export default Sections;

export const InsuranceCTA = () => (
  <section className="py-24 bg-[#c8102e] text-white relative overflow-hidden">
    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url(${heroImages.bg})`, backgroundSize: 'cover' }} />
    <div className="relative max-w-4xl mx-auto px-6 text-center">
      <div className="flex items-center gap-3 justify-center mb-4"><span className="h-px w-10 bg-white" /><span className="eyebrow text-white">Why Choose Us</span><span className="h-px w-10 bg-white" /></div>
      <h2 className="font-serif-display text-4xl md:text-6xl font-bold mb-6 leading-[1.05]">Sending love is now safer than ever.</h2>
      <p className="text-white/90 max-w-2xl mx-auto mb-3 text-lg leading-relaxed">
        Every insured package includes complimentary personal accident cover of up to <span className="font-bold underline decoration-2 underline-offset-4">LKR 1 Million</span> for your loved ones.
      </p>
      <p className="text-white/70 max-w-xl mx-auto mb-10">Experience premium peace of mind with every shipment we handle.</p>
      <Link to="/contact" className="group inline-flex items-center gap-3 bg-white text-black hover:bg-black hover:text-white font-semibold h-13 px-10 py-4 text-[13px] tracking-[0.15em] uppercase transition-colors">
        Talk to Our Team <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </Link>
    </div>
  </section>
);
